// Generated by tutorials/record.cjs from tutorials/articles/h55-tables.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h55 --text-only` (wording) or
// `node tutorials/record.cjs h55` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'tables',
  catalogId: 'h55',
  title: 'Tables: the lists behind Dietech Online',
  summary: 'Tables holds the lists you pick from everywhere else: food allergies, special needs and devices, units, food groups, dining rooms and more. Find a table, add and change rows, and see which rows come from PSI.',
  audience: 'Administrators and dietary managers who keep the facility\'s lists up to date',
  area: 'food-data',
  learn: [
    'Find a table by its section or by searching',
    'Which tables facilities use most',
    'Add a row and change one',
    'Usage, History, Replace and Export',
    'Which rows come from PSI, and what you can change on them',
  ],
  keywords: [
    'tables',
    'table',
    'lookup tables',
    'lists',
    'drop-down lists',
    'special devices',
    'special device',
    'special needs',
    'food allergies',
    'item units',
    'units of measure',
    'food groups',
    'menu categories',
    'dining rooms',
    'physician',
    'doctors',
    'note types',
    'FAC',
    'PSI',
    'matches PSI',
    'PSI match',
    'add a row',
    'export',
    'usage',
    'replace',
  ],
  steps: [
    {
      id: 'hub',
      part: 'Find a table',
      heading: 'Open Tables',
      text: 'Select Food Data in the menu on the left, then Tables.\n\nThese are the lists you pick from everywhere else: diet types, allergies, units, dining rooms and more. They are grouped in sections, such as Diet Orders, Allergies & Adjustments and Resident Profile, and each card says what the table holds.',
      media: {
        image: {
          src: '/help/tables/hub.webp',
          width: 1280,
          height: 842,
        },
        alt: 'The Tables page: a Search tables box, then cards under Diet Orders (Consistencies, Consistency Combinations, Diet List Sequence, Diet Types, Feeding Status, Tube Feed Products) and Allergies & Adjustments.',
      },
    },
    {
      id: 'search',
      part: 'Find a table',
      heading: 'Search for a table',
      text: 'Type in Search tables. It looks at what each table holds as well as its name, so "allerg" finds Food Allergies and Allergy Groups.',
      media: {
        image: {
          src: '/help/tables/search.webp',
          width: 1280,
          height: 990,
        },
        clip: {
          src: '/help/tables/search.mp4',
          width: 1280,
          height: 990,
          durationSec: 3.8,
        },
        alt: 'Search tables with "allerg" typed in, and the cards Allergy Groups and Food Allergies under Allergies & Adjustments.',
      },
    },
    {
      id: 'open',
      part: 'Find a table',
      heading: 'Open a table',
      text: 'Select a card to open the table. The list on the left has every table you can open, in the same sections: select one to move to it, type in Filter tables to narrow the list, or select a section heading to fold it away.',
      media: {
        image: {
          src: '/help/tables/open.webp',
          width: 1280,
          height: 990,
        },
        clip: {
          src: '/help/tables/open.mp4',
          width: 1280,
          height: 990,
          durationSec: 2.8,
        },
        alt: 'The Food Allergies table, with the list of tables on the left under Diet Orders, Allergies & Adjustments and Resident Profile.',
      },
    },
    {
      id: 'resident-tables',
      part: 'The tables you will use most',
      heading: 'Tables for residents',
      text: 'Most facilities keep a handful of tables up to date:\n\n- Food Allergies: the allergies you can record on a resident, each linked to what keeps the food off the tray. See Checking allergy protection.\n- Special Needs: needs such as "Needs assistance", with the text printed on the tray ticket.\n- Special Devices: adaptive devices such as a built-up spoon. Your facility may call them Devices or Equipment.\n- Physician (or Doctors): the doctors and other prescribers you pick on a resident.\n- Note Types: the types a resident note is filed under.',
      media: {
        image: {
          src: '/help/tables/resident-tables.webp',
          width: 1280,
          height: 990,
        },
        clip: {
          src: '/help/tables/resident-tables.mp4',
          width: 1280,
          height: 990,
          durationSec: 4.5,
        },
        alt: 'Tables scrolled to Allergies & Adjustments (Adjustment Global Groups, Allergy Groups, Food Allergies, Meal Pattern Global Groups) and Resident Profile (IBW, Medication Effects, Note Types, Special Device, Special Needs).',
      },
    },
    {
      id: 'kitchen-tables',
      part: 'The tables you will use most',
      heading: 'Tables for items, menus and the kitchen',
      text: 'For items, menus and the kitchen:\n\n- Item Units: the units items and recipes are measured in, with their plurals and ounce conversions.\n- Food Groups: the groups and subgroups items are filed under.\n- Menu Categories: the categories menu items are grouped under, and the name printed on menus.\n- Dining Rooms: each dining room and its meal times.\n\nDiet Types and Consistencies are covered in Diet configuration.',
      media: {
        image: {
          src: '/help/tables/kitchen-tables.webp',
          width: 1280,
          height: 498,
        },
        alt: 'The Items & Menus section of Tables with the cards Food Groups, Garnish, Item Linking, Item Types, Item Units, Menu Categories, Menu Category Grouping, Portions and Service Items.',
      },
    },
    {
      id: 'most-used-done',
      part: 'The tables you will use most',
      heading: 'Keep these few up to date',
      text: 'Keep these few up to date as your residents and kitchen change. Most of the other tables are set up once, often with Dietech support, and rarely change.',
      textOnly: true,
    },
    {
      id: 'new-row',
      part: 'Add a row',
      heading: 'Add a row',
      text: 'Open the table, such as Special Device, and select New. Type the Name; on this list it is kept in capitals.\n\nThe boxes below depend on the table. On Special Device, Do Not Tally leaves the device out of the special device counts on production reports.',
      media: {
        image: {
          src: '/help/tables/new-row.webp',
          width: 944,
          height: 452,
        },
        clip: {
          src: '/help/tables/new-row.mp4',
          width: 944,
          height: 452,
          durationSec: 4.4,
        },
        alt: 'The New Special Device box with the Name WEIGHTED SPOON typed in, and the Print Label and Do Not Tally boxes.',
      },
    },
    {
      id: 'save-row',
      part: 'Add a row',
      heading: 'Save',
      text: 'Select Save. The new row is on the list, and staff can pick it on a resident\'s record right away.',
      media: {
        image: {
          src: '/help/tables/save-row.webp',
          width: 1280,
          height: 990,
        },
        clip: {
          src: '/help/tables/save-row.mp4',
          width: 1280,
          height: 990,
          durationSec: 3.1,
        },
        alt: 'The Special Device table with WEIGHTED SPOON added to the list.',
      },
    },
    {
      id: 'modify',
      part: 'Change a row',
      heading: 'Change a row',
      text: 'Double-click a row, or select it and then Open. Change what you need and select Save.\n\nResidents and items point to the row, so a new name shows everywhere it is used at once.',
      media: {
        image: {
          src: '/help/tables/modify.webp',
          width: 944,
          height: 452,
        },
        clip: {
          src: '/help/tables/modify.mp4',
          width: 944,
          height: 452,
          durationSec: 2.9,
        },
        alt: 'The Edit Special Device box for Divided Plate with Do Not Tally checked.',
      },
    },
    {
      id: 'more',
      part: 'Change a row',
      heading: 'More actions',
      text: 'Select a row, then More actions (the three dots at the top right):\n\n- Usage: the residents, items and menus that use the selected row. Check it before you change or remove a row.\n- History: who changed the row, when, and what changed; another History item lists rows that were deleted.\n- Replace: moves everything that uses the selected row to the one you pick, then removes it. Use it to merge two rows that mean the same thing.\n- Export as CSV or Excel.\n- Delete.\n\nThe items depend on the table and on your role.',
      media: {
        image: {
          src: '/help/tables/more.webp',
          width: 376,
          height: 532,
        },
        clip: {
          src: '/help/tables/more.mp4',
          width: 376,
          height: 532,
          durationSec: 2.3,
        },
        alt: 'The More actions menu on Special Device with Usage, Print, History (Selected Item), History (Deleted), Replace, Export as CSV, Export as Excel and Delete.',
      },
    },
    {
      id: 'change-done',
      part: 'Change a row',
      heading: 'Tell the other facilities',
      text: 'Tables are shared by every facility in your organization. A new name, a Replace or a Delete reaches all of them, so tell the other facilities first.',
      textOnly: true,
    },
    {
      id: 'fac',
      part: 'Rows that come from PSI',
      heading: 'Your rows and PSI\'s rows',
      text: 'Some tables start with rows PSI supplies, and your facility adds its own. Item Units is one: a check in the FAC column means your facility added the row. Rows without it come from PSI.\n\nPSI\'s units are what recipes and nutrition are measured in, so they stay as PSI keeps them.',
      media: {
        image: {
          src: '/help/tables/fac.webp',
          width: 1280,
          height: 990,
        },
        alt: 'The Item Units table with its FAC column checked for Half Pan and Mini Muffin, and unchecked for PSI\'s units such as Cup, Ounce and Slice.',
      },
    },
    {
      id: 'psi-row',
      part: 'Rows that come from PSI',
      heading: 'What you can change on a PSI row',
      text: 'Open a PSI row, such as Cup. Its name, abbreviations, ounce conversion and the other options are greyed out; only Combine Portion and Size can be changed. A PSI row cannot be deleted.\n\nThe same goes for PSI\'s rows on Medication Effects, Tube Feed Products, Preparation Types and Recipe Categories: they cannot be deleted, and on Preparation Types they cannot be changed. Rows your facility added can always be changed.',
      media: {
        image: {
          src: '/help/tables/psi-row.webp',
          width: 944,
          height: 998,
        },
        alt: 'The Edit Item Unit box for Cup with Unit Name, Abbreviation, Plural Name, Plural Abbreviation, Oz Conversion, Fluid and Scoop greyed out, and Combine Portion and Size still available.',
      },
    },
    {
      id: 'psi-match',
      part: 'Rows that come from PSI',
      heading: 'Matching your rows to PSI\'s',
      text: 'When PSI sends recipes, nutrition and allergens, they have to land on the right rows. So each of your consistencies, item types and allergy groups can be matched to the PSI entry it stands for.\n\nYou set that match on Data Update, under Setup: when an update meets a row it cannot match, it asks you to pick PSI\'s entry, and Allergy matching lists your allergy groups. There is no "Matches PSI" column on Tables. Changing a row on Tables keeps its match.',
      textOnly: true,
    },
    {
      id: 'psi-only',
      part: 'Rows that come from PSI',
      heading: 'What only PSI changes',
      text: 'PSI\'s recipes and ingredients, and the nutrition that comes with them, change only when PSI sends an update through Data Update. On Tables, Ingredients and Recipe Nutrients are lists to look things up in, and Plan of Care Report Types is read only.\n\nIf a PSI row looks wrong, contact Dietech support rather than adding a copy of it.',
      textOnly: true,
    },
  ],
  tips: [
    'Type part of a name or what the table is for in Search tables: "allerg" finds Food Allergies and Allergy Groups, "temperature" finds Food Storage.',
    'Click a section heading in the list on the left to fold it away. Dietech Online remembers which sections you folded.',
    'Before you change or remove a row that residents use, select it and use More actions, Usage.',
  ],
  notes: [
    'You see only the tables your role allows, and New, Open and Delete only at the levels your role has for that table. Some tables show only when your facility uses production menus. If a table or button is missing, ask your administrator.',
    'Tables are shared by every facility in your organization, so a change reaches all of them.',
    'Some names follow your facility\'s own words: if your facility calls a special device a "Device" or a doctor a "Physician", the table is named that way.',
    'Diet Types, Consistencies and how they combine are covered in Diet configuration.',
    'Ingredients and Recipe Nutrients are lists to look things up in. Nutrition always comes from recipes; it is never typed into a table.',
  ],
  related: [
    'diet-configuration',
    'allergies-likes-dislikes',
    'allergy-protection-check',
    'items-and-recipes',
    'getting-started',
    'roles-and-access',
  ],
  recorded: '2026-11-03',
};

export default article;
